import { useWidgetContext } from '../../context/WidgetContext'
import { useMemo } from 'react'
import { MainActions } from '../common/block/MainActions'
import { WidgetFooterActions } from '../common/block/WidgetFooterActions'
import { MainHeader } from '../common/block/MainHeader'
import { AgentTagActions } from '../common/block/AgentTagActions'
import { AgentInfoRow } from '../common/block/AgentInfoRow'
import { PanelContainer } from '../common/block/PanelContainer'
import { isFullscreenWidgetMode } from '../../types/agent-mode'
import type { CSSProperties } from 'react'

type Props = {
  isSmall: boolean
  bodyWidth: number
}

function Widget({ isSmall }: Props) {
  const {
    isWidgetOpen,
    isDocActive,
    isApiActive,
    isAIOpen,
    isModeFullscreen,
    mode,
    agent,
    config,
  } = useWidgetContext()

  // This is your existing "active" meaning: doc/api/ai is open
  const isModalOpen = useMemo(
    () => (isAIOpen || isDocActive || isApiActive),
    [isAIOpen, isDocActive, isApiActive]
  )

  // ✅ Keep your existing meaning for is-active
  const activeClass = isModalOpen ? 'is-active' : ''
  const bodyClass = isSmall ? 'is-small' : ''

  // ✅ Controls whether the widget is visible (without unmounting)
  const openClass = isWidgetOpen ? 'is-open' : 'is-closed'
  const speechFullscreenClass =
    isFullscreenWidgetMode(mode) && isAIOpen && isModeFullscreen ? 'speech-fullscreen' : ''
  const chatFullscreenClass = mode === 'chat' && config.fullscreenChat && isAIOpen ? 'chat-fullscreen' : ''
  const overlayStyle = useMemo<CSSProperties>(() => {
    const placement = (config as any)?.placement
    if (!placement || typeof placement !== 'object') return {}

    const next: CSSProperties = {}
    if (typeof placement.position === 'string') next.position = placement.position
    if (typeof placement.top === 'string') next.top = placement.top
    if (typeof placement.right === 'string') next.right = placement.right
    if (typeof placement.bottom === 'string') next.bottom = placement.bottom
    if (typeof placement.left === 'string') next.left = placement.left
    if (typeof placement.zIndex === 'number' || typeof placement.zIndex === 'string') {
      next.zIndex = placement.zIndex
    }
    return next
  }, [config])

  return (
    <div
      className={`osirus-agent-overlay ${openClass} ${activeClass} ${bodyClass} ${speechFullscreenClass} ${chatFullscreenClass} mode-${mode}`}
      style={overlayStyle}
    >
      <div className={`osirus-agent-content ${activeClass} ${bodyClass} ${speechFullscreenClass} ${chatFullscreenClass} mode-${mode}`}>
        <div className={`${isModalOpen ? 'is-docActive' : ''}`}>
          <div className="osirus-agent-flyout">
            {isModalOpen && <PanelContainer />}

            <div className={`osirus-agent-container ${bodyClass}`}>
              <div>
                <MainHeader />
                <AgentTagActions />

                {agent.schedule && (
                  <AgentInfoRow
                    iconName="clock"
                    label="Hours"
                    value={agent.schedule}
                  />
                )}

                <MainActions />
              </div>

              <WidgetFooterActions />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Widget
